/*
  Campaign popup behavior (MerchantFlow addon for Emine Aydin theme)

  Behavior:
  - Shows once per visitor, then stays hidden for `frequency-days` days.
    Tracked in localStorage (timestamp of last dismissal/view).
  - Opens `delay` seconds after first load.
  - If `autoclose` > 0, closes automatically after that many seconds.
  - Closes on: X button, backdrop click, ESC.
  - Accessibility: role="dialog" + aria-modal + focus trap + focus restore.
*/
(function () {
  'use strict';

  var STORAGE_KEY = 'emine_campaign_popup_last_seen';

  function readLastSeen() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return 0;
      var ts = parseInt(raw, 10);
      return isNaN(ts) ? 0 : ts;
    } catch (e) {
      return 0;
    }
  }

  function markSeen() {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch (e) {
      /* private mode / storage disabled: fail silently, popup just won't persist */
    }
  }

  function shouldShow(frequencyDays) {
    var last = readLastSeen();
    if (!last) return true;
    var days = frequencyDays > 0 ? frequencyDays : 7;
    var elapsed = Date.now() - last;
    return elapsed >= days * 24 * 60 * 60 * 1000;
  }

  function initPopup(root) {
    var overlay = root.querySelector('[data-campaign-overlay]');
    if (!overlay) return;

    var card = overlay.querySelector('[data-campaign-card]');
    var closeButtons = overlay.querySelectorAll('[data-campaign-close]');

    var delaySeconds = parseFloat(root.getAttribute('data-delay')) || 0;
    var autocloseSeconds = parseFloat(root.getAttribute('data-autoclose')) || 0;
    var frequencyDays = parseFloat(root.getAttribute('data-frequency-days')) || 7;

    var openTimer = null;
    var autocloseTimer = null;
    var lastFocused = null;
    var isOpen = false;

    function focusableElements() {
      return Array.prototype.slice.call(
        overlay.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        )
      );
    }

    function onKeydown(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        close();
        return;
      }
      if (e.key === 'Tab') {
        var focusables = focusableElements();
        if (!focusables.length) return;
        var first = focusables[0];
        var last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    function open() {
      if (isOpen) return;
      isOpen = true;
      lastFocused = document.activeElement;
      overlay.setAttribute('data-open', '');
      overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', onKeydown);

      // Persist as soon as it is shown, so a refresh won't re-trigger it.
      markSeen();

      var focusables = focusableElements();
      var target = closeButtons[0] || focusables[0] || card;
      if (target && typeof target.focus === 'function') {
        target.focus();
      }

      if (autocloseSeconds > 0) {
        autocloseTimer = window.setTimeout(close, autocloseSeconds * 1000);
      }
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      overlay.removeAttribute('data-open');
      overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeydown);
      if (autocloseTimer) {
        window.clearTimeout(autocloseTimer);
        autocloseTimer = null;
      }
      // markSeen already called on open; ensure it in case open persistence failed.
      markSeen();
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
    }

    Array.prototype.forEach.call(closeButtons, function (btn) {
      btn.addEventListener('click', close);
    });

    // Backdrop click (only when the overlay itself, not the card, is clicked)
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) close();
    });

    if (!shouldShow(frequencyDays)) return;

    openTimer = window.setTimeout(open, Math.max(0, delaySeconds) * 1000);
  }

  function boot() {
    var root = document.querySelector('[data-campaign-popup]');
    if (root) initPopup(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
